import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FaRegFolderOpen, FaRegCheckCircle } from "react-icons/fa";
import { Select } from "antd";
import toast from "react-hot-toast";
import {
  getFinalDocuments,
  generateFinalDocuments,
} from "../../services/FinalDocumentServices";
import config from "../../config";

const BUTTON_SPINNER_STYLE = {
  width: "0.85rem",
  height: "0.85rem",
  borderWidth: "0.15em",
  verticalAlign: "-0.1em",
};

const BODY_SPINNER_STYLE = {
  width: "1.75rem",
  height: "1.75rem",
  borderWidth: "0.2em",
};

const GenerateFinalDocumentModal = ({ show, handleClose, record }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [loader, setLoader] = useState("");
  const [errors, setErrors] = useState({});

  const employeeName = record?.employee_name || "";

  useEffect(() => {
    if (!show) return;

    setSelectedIds([]);
    setErrors({});

    const fetchDocuments = async () => {
      setFetching(true);
      try {
        const response = await getFinalDocuments(record?.employee_id);
        setDocuments(response?.data || []);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Unable to load final documents"
        );
        setDocuments([]);
      } finally {
        setFetching(false);
      }
    };

    fetchDocuments();
  }, [show, record]);

  const downloadDocument = async (file) => {
    const response = await fetch(`${config.IMAGE_URL}${file.file_url}`);
    if (!response.ok) throw new Error(`Unable to download ${file.document_name}`);

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = objectUrl;
    anchor.download = file.file_name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const downloadAll = async (generated) => {
    for (const file of generated) {
      try {
        await downloadDocument(file);
        await new Promise((resolve) => setTimeout(resolve, 400));
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (selectedIds.length === 0) {
      newErrors.documents = "Select at least one document.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAction = async (action) => {
    if (!validate()) return;

    setLoader(action);

    try {
      const response = await generateFinalDocuments({
        employee_id: record?.employee_id,
        fnf_id: record?.fnf_id,
        document_ids: selectedIds,
        action,
      });

      const generated = response?.data?.documents || [];

      if (action === "save") {
        toast.success(
          `${generated.length} document${generated.length > 1 ? "s" : ""} generated`
        );
        await downloadAll(generated);
      } else {
        toast.success(response?.message || "Documents sent successfully");
      }

      handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to generate documents"
      );
    } finally {
      setLoader("");
    }
  };

  const busy = loader !== "";
  const pendingDocuments = documents.filter((item) => !item.is_generated);
  const personalEmail = record?.personal_email;

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <div>
          <h5 className="fw-bold mb-0">Generate Documents</h5>
          {employeeName && (
            <div className="text-muted small mt-1">{employeeName}</div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {fetching ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <Spinner animation="border" variant="primary" style={BODY_SPINNER_STYLE} />
            <div className="text-muted small mt-3">Loading documents...</div>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-5">
            <FaRegFolderOpen className="text-muted mb-2" size={28} />
            <div className="text-muted small">
              No documents are configured for this company.
            </div>
          </div>
        ) : pendingDocuments.length === 0 ? (
          <div className="text-center py-5">
            <FaRegCheckCircle className="text-success mb-2" size={28} />
            <div className="text-muted small">
              All documents have already been generated for this employee.
            </div>
          </div>
        ) : (
          <>
            <label className="form-label fw-semibold mb-2">
              Select documents
            </label>
            <div style={{ position: "relative", zIndex: 1 }}>
              <Select
                mode="multiple"
                style={{ width: "100%", minHeight: "38px" }}
                placeholder="Select one or more documents to generate"
                value={selectedIds}
                onChange={(value) => setSelectedIds(value || [])}
                options={documents.map((item) => ({
                  value: item.id,
                  label: item.is_generated
                    ? `${item.document_name} (already generated)`
                    : item.document_name,
                  disabled: Boolean(item.is_generated),
                }))}
                showSearch
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                allowClear
                disabled={busy}
                status={errors.documents ? "error" : ""}
                getPopupContainer={(trigger) => trigger.parentElement || document.body}
                dropdownStyle={{ zIndex: 9999 }}
              />
            </div>

            {errors.documents && (
              <div className="text-danger small mt-1">{errors.documents}</div>
            )}

            <div className="text-muted small mt-3">
              {personalEmail ? (
                <>
                  Email will be sent to <strong>{personalEmail}</strong>
                </>
              ) : (
                "No personal email address on record, so this employee cannot be emailed."
              )}
            </div>

          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0">
        <Button
          variant="light"
          size="sm"
          onClick={handleClose}
          disabled={busy}
        >
          Close
        </Button>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => handleAction("save")}
          disabled={busy || fetching || pendingDocuments.length === 0}
        >
          {loader === "save" ? (
            <>
              <Spinner
                animation="border"
                className="me-2"
                style={BUTTON_SPINNER_STYLE}
              />
              Generating...
            </>
          ) : (
            "Save"
          )}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleAction("email")}
          disabled={
            busy || fetching || pendingDocuments.length === 0 || !personalEmail
          }
          title={
            personalEmail
              ? `Send to ${personalEmail}`
              : "No personal email address on record for this employee"
          }
        >
          {loader === "email" ? (
            <>
              <Spinner
                animation="border"
                className="me-2"
                style={BUTTON_SPINNER_STYLE}
              />
              Sending...
            </>
          ) : (
            "Send Email"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateFinalDocumentModal;
